import { motion, AnimatePresence } from 'framer-motion';
import { Progress } from '@/components/ui/progress';
import { useEffect, useState } from 'react';
import { Calculator } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface ProgressiveLoadingProps {
  firstName: string;
  onComplete: () => void;
}

export function ProgressiveLoading({ firstName, onComplete }: ProgressiveLoadingProps) {
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState(0);
  const { t } = useLanguage();

  const steps = [
    firstName ? `${t("Hi")} ${firstName}! ${t("Getting your personalized quotes...")}` : t("Getting your personalized quotes..."),
    t("Analyzing rates from top Canadian insurers..."),
    t("Finding your best coverage options..."),
    t("Almost ready with your personalized rates!")
  ];

  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    const stepInterval = setInterval(() => {
      setStep(prev => {
        if (prev >= steps.length - 1) {
          clearInterval(stepInterval);
          return prev;
        }
        return prev + 1;
      });
    }, 1000);

    const timer = setTimeout(() => {
      onComplete();
    }, 3000);

    return () => {
      clearInterval(progressInterval);
      clearInterval(stepInterval);
      clearTimeout(timer);
    };
  }, [steps.length, onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="max-w-xl mx-auto text-center space-y-8 py-12"
    >
      <div className="flex justify-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-[#494d62] p-4 rounded-full"
        >
          <Calculator className="w-8 h-8 text-white" />
        </motion.div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="min-h-[2rem]"
        >
          <p className="text-lg font-medium text-[#494d62]">{steps[step]}</p>
        </motion.div>
      </AnimatePresence>

      <div className="space-y-2">
        <Progress value={progress} className="h-2" />
        <p className="text-sm text-[#494d62]/70">
          {Math.round(progress)}% {t("complete")}
        </p>
      </div>
    </motion.div>
  );
}